import { Component, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { ConnectionService, OfflineModeService, ViewportService, StorageService, UpdateService, CapabilitiesCacheService, ProfileModalComponent, PROFILE_API, IProfileApi, SelectionService, GeomEditorService, ProjektUebersichtComponent, OfflineAbgleichService, ZEITGRENZE, mitZeitgrenze,
	ConsoleErrorService,
} from '@luzula/luzula-lib';
import { DatabaseConfigService } from '../database-config.service';
import { DataService } from '../data.service';
import { Command, NavbarService } from '../navbar.service';
import { isPointDetailUrl } from './is-point-detail-url';
import { AuthService as AuthorizationService } from '../auth.service';
import { canAccessListenverwaltung, canAccessBenutzerverwaltung, canAccessDruckLayout, canAccessDatenbankverwaltung } from '../role-model';
import { ProjectService } from '../project.service';
import { ChangelogService } from '../changelog/changelog.service';
import { ChangelogComponent } from '../changelog/changelog.component';
import { map, of } from 'rxjs';
import pkg from '../../../package.json';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../../environments/environment';

@Component({
    standalone: false,
    selector: 'bp-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.css'],

})
export class NavbarComponent {
	router = inject(Router);
	activeRoute = inject(ActivatedRoute);
	version = pkg.version;

	isOnline = navigator.onLine;
	showDetails = false;

	C = Command;
	dbConfig = inject(DatabaseConfigService);
	/** Backend-URL (apiBaseUrl) fuers Dev-Banner: zeigt, welche API/DB tatsaechlich bedient wird (localhost vs. dev-api vs. prod). */
	apiBaseUrl = environment.apiBaseUrl;

	private updateTitle() {
		this.ns.title = (this.ns.componentTitle);
	}

	aus = inject(AuthorizationService);
	ds = inject(DataService);
	ns = inject(NavbarService);
	cs = inject(ConnectionService);
	storage = inject(StorageService);
	us = inject(UpdateService);
	oms = inject(OfflineModeService);
	ps = inject(ProjectService);
	ss = inject(SelectionService);
	geomEditor = inject(GeomEditorService);
	vps = inject(ViewportService);
	cls = inject(ChangelogService);
	/** Der Rueckweg aus dem Offline-Modus — einmal fuer alle Apps (siehe `onSync`). */
	private offlineAbgleich = inject(OfflineAbgleichService);
	private ces = inject(ConsoleErrorService);
	private modalService = inject(NgbModal);
	private capabilities = inject(CapabilitiesCacheService);
	private profileApi = inject(PROFILE_API, { optional: true }) as IProfileApi | null;

	/**
	 * App-Verwaltungs-Sichtbarkeit (PERMISSIONS.md §8) — Single-Source-Regel aus
	 * role-model.ts, damit Navbar und Route-Guard nie auseinanderlaufen. Da
	 * canIbe synchron ist, wird der Rollen-Anteil beim canManage$-Emit kombiniert
	 * — bei Login/Logout emittiert canManage$ neu und die Rollen sind dann aktuell.
	 *
	 * - Benutzerverwaltung: Admin ODER Entwickler (NICHT Manager)
	 * - Listenverwaltung:   Admin ODER Entwickler ODER Manager
	 */
	userManagementEnabled$ = this.capabilities.canManage$.pipe(
		map(admin => canAccessBenutzerverwaltung(admin, r => this.aus.canIbe(r)))
	);

	listenverwaltungEnabled$ = this.capabilities.canManage$.pipe(
		map(admin => canAccessListenverwaltung(admin, r => this.aus.canIbe(r)))
	);

	/**
	 * Wird nach Login per /me/profile gesetzt. Ans <lz-avatar> als hasAvatar
	 * weitergereicht, damit der Avatar-Endpunkt nicht fuer nicht-vorhandene
	 * Bilder mit 404 antworten muss (sonst fail2ban-relevanter Burst).
	 *
	 * Initial false: zeigt sofort die Initialen-Bubble, kein Avatar-Request
	 * beim allerersten Render. Sobald /me/profile zurueckkommt, wird der
	 * Wert ggf. auf true gehoben — AvatarComponent laedt dann das Bild nach.
	 */
	hasOwnAvatar = false;

	constructor() {
		// Capabilities erst nach Login abrufen — sonst feuert der Request ohne
		// Bearer-Token und das Backend antwortet 401 (Cache merkt sich den Fehler).
		// loggedIn ist das richtige Signal: feuert genau dann, wenn Keycloak die
		// Auth abgeschlossen hat und der Token verfuegbar ist. Das BehaviorSubject
		// `isAuthenticated` wird im Offline-Fallback zu frueh true (cached Info,
		// noch kein Token).
		this.aus.loggedIn.subscribe(() => {
			this.capabilities.refresh().subscribe();
			this.refreshOwnAvatarFlag();
		});

		this.cs.monitor().subscribe((isOnline: boolean) => {
			this.isOnline = isOnline;
			if (isOnline) this.us.checkForUpdates();
			this.updateTitle();
		});
		const detailsFunc = (url: string) => {
			if (url === '/bohrpunkte') {
				this.showDetails = true;
			}
			else {
				this.showDetails = false;
				this.updateTitle();
			}
		};
		this.router.events.subscribe(
			(event: any) => {
				if (event instanceof NavigationEnd) {
					detailsFunc(this.router.url);
				}
			}
		);
		detailsFunc(this.router.url);

		// Nach „Neu anmelden" von selbst weiter (Nutzer-Entscheidung 18.09.2026).
		void this.setzeAbgleichFort();
	}

	/**
	 * Stand vor der Anmeldung ein Abgleich an, geht er jetzt weiter.
	 *
	 * Die Anmeldung mit Kennwort laedt die App neu; bis 18.09.2026 musste der
	 * Nutzer danach den ganzen Weg noch einmal gehen (Leiste, Dialog, Vorschau,
	 * Fenster). Den Vermerk dazu schreibt der Abgleich-Dienst der Bibliothek
	 * vor dem Sprung zur Anmeldung.
	 *
	 * Erst, wenn der Datendienst bereit ist: Der Abgleich braucht die lokale
	 * Datenbank, und die wird beim Start erst noch geoeffnet. Kommt der Dienst
	 * nicht hoch, passiert nichts — der Knopf in der Leiste bleibt.
	 */
	private async setzeAbgleichFort(): Promise<void> {
		try {
			await mitZeitgrenze(this.ds.waitForInitialization(), ZEITGRENZE.dienstBereit, 'Datendienst bereit');
		} catch (err) {
			// Nach 30 s ohne bereiten Datendienst geht der Abgleich nach der
			// Anmeldung stillschweigend NICHT weiter — die App sieht normal aus,
			// und die Arbeit bleibt liegen. Das gehoert gemeldet.
			console.warn('[Navbar] Abgleich nicht fortgesetzt, der Datendienst wurde nicht bereit:', err);
			this.ces.melde('[Abgleich] Datendienst wurde nicht bereit', err);
			return;
		}
		await this.offlineAbgleich.fortsetzenNachAnmeldung();
	}

	canEdit() {
		if (this.oms.offlineMode()) {
			return of(true);
		}
		else {
			return of(this.ps.canI('write', 'points'));
		}
	}

	title(): string {
		this.updateTitle();
		return this.ns.getTitle();
	}

	async onHomeClick(event?: MouseEvent) {
		// Der Dialog steht in der Lib (frageUndAktualisiere) und nennt dort auch
		// die laufende und die neue Fassung. Vorher war er hier nachgebaut - und
		// bekam die Versionszeilen nicht mit.
		if (await this.us.frageUndAktualisiere()) return;
		if (this.router.url === '/') {
			if (event && event.ctrlKey)
				this.storage.cleanUp();
			else if (this.oms.offlineMode())
				return; // Im Offline-Modus kein Reload - SW könnte alte Version ausliefern
			else
				window.location.reload();
		}
		else
			this.router.navigate(['/']);
	}
	
	onGpsHome() {
		this.ns.command(Command.gpsHome).emit();
	}

	currentUser() {
		return this.aus.currentUser;
	}

	onLoggedOut() {
		this.aus.onLoggedOut();
		// Aktives Projekt entladen beim expliziten Ausloggen
		this.ps.unloadCurrentProject();
	}
	
	openManual() {
		window.open('/assets/bpweb.pdf', '_blank');
	}
	
	activeFilter(): boolean {
		return !!this.storage.getSync('currentFilter');
	}

	hasProject(): boolean {
		return !!this.ps.project();
	}

	/**
	 * True, sobald mindestens ein Punkt in der Liste ausgewählt ist. Steuert die
	 * Sichtbarkeit von „Auswahl löschen" — der Eintrag erscheint nur, wenn es
	 * tatsächlich etwas zu löschen gibt. Wird beim Öffnen des Dropdowns (Klick →
	 * Change-Detection) ausgewertet, spiegelt also den aktuellen Auswahlstand.
	 */
	hasSelection(): boolean {
		return this.ss.count() > 0;
	}

	/**
	 * True nur auf einer echten Protokoll-Route (`/bohrpunkte/:id…`). Bindet den
	 * „Punkt Speichern"-Knopf zusätzlich an die Route, damit er außerhalb eines
	 * Protokolls nie sichtbar ist — auch wenn das `savePoint`-Navbar-Kommando in
	 * einem ungünstigen Verlassen-Pfad (Route-Caching der Karte) hängenbleibt.
	 */
	onPointRoute(): boolean {
		return isPointDetailUrl(this.router.url);
	}

	/**
	 * Vor der Anmeldung dürfen im Dropdown-Menü nur "Handbuch" und der
	 * Auth-Button erscheinen. Einträge wie "Neuer Punkt" sind auch dann
	 * sichtbar, wenn ein currentProject aus der lokalen DB restored
	 * wurde (z. B. nach Session-Ablauf) — genau das verhindert dieses Flag.
	 */
	isAuthenticated(): boolean {
		return !!this.aus.currentUser;
	}

	isDeveloper(): boolean {
		return this.ps.canIbe('entwickler');
	}

	/**
	 * Darf der Flächen-Editor benutzt werden? Fragt dieselbe Stelle wie die
	 * Konfliktklärung — wird das Recht eines Tages erweitert, gilt das sofort
	 * für beides (Auflage des Nutzers 11.09.2026).
	 */
	darfFlaechenBearbeiten(): boolean {
		return this.ps.canEditGeomFeatures();
	}

	/**
	 * Öffnet die app-übergreifende Projekt- und Nutzungsübersicht (luzula-lib).
	 * Der Menüeintrag dazu hängt an {@link isDeveloper} und wird für alle anderen
	 * gar nicht gerendert — nicht ausgegraut.
	 */
	openProjektUebersicht(): void {
		this.modalService.open(ProjektUebersichtComponent, { size: 'xl', scrollable: true, centered: true });
	}

	/**
	 * Druck-Layout-Zugang (PERMISSIONS.md §8) — NUR Entwickler. Single-Source-Regel
	 * aus role-model.ts, gleiche canIbe-Quelle (aus) wie der Route-Guard, damit
	 * Navbar-Ausgrauen und Route-Sperre nie auseinanderlaufen.
	 */
	canSeeDruckLayout(): boolean {
		return canAccessDruckLayout(r => this.aus.canIbe(r));
	}

	/**
	 * Datenbankverwaltungs-Zugang (PERMISSIONS.md §8) — NUR Entwickler. Single-
	 * Source-Regel aus role-model.ts, gleiche canIbe-Quelle (aus) wie der Route-
	 * Guard, damit Navbar-Sichtbarkeit und Route-Sperre nie auseinanderlaufen.
	 */
	canSeeDatenbankverwaltung(): boolean {
		return canAccessDatenbankverwaltung(r => this.aus.canIbe(r));
	}

	isProjectManager(): boolean {
		return this.ps.canIbe('manager');
	}

	isGuest(): boolean {
		return this.aus.isGuest();
	}

	/** Prüft ob Netzwerk im Online-Modus nicht verfügbar ist */
	networkUnavailable(): boolean {
		return !this.oms.userOfflineMode() && !this.oms.networkOnline();
	}

	/**
	 * Der Weg zurueck aus dem Offline-Modus.
	 *
	 * **Das ist der einzige.** Bis zum 08.09.2026 stand hier daneben ein zweiter
	 * (`onExitOfflineMode` mit `ds.exitOfflineMode()`) — von keinem Knopf und
	 * keiner Stelle im Code aufgerufen. Er hat beim Suchen zweimal in die Irre
	 * gefuehrt und ist deshalb entfernt.
	 *
	 * **Der Ablauf steht in der Bibliothek** ({@link OfflineAbgleichService}).
	 * Vorher war er hier ausgeschrieben, ein zweites Mal im Projektmenue der
	 * Karte und ein drittes Mal in der Biotopkartierung — drei Fassungen, die
	 * auseinandergedriftet waren. Was bpweb hier fehlte:
	 *
	 * - der **Vorlauf** (Verbindung frisch messen, Anmeldung erneuern, BEVOR
	 *   gefragt wird) — genau daran hing der Befund vom 08.09.2026, dass nach
	 *   tagelanger Offline-Arbeit nichts mehr hochging;
	 * - die **Vorwarnung**, wenn im Buero an denselben Punkten gearbeitet wurde;
	 * - ein Fehlertext, der die **abgelaufene Anmeldung** benennt und „Neu
	 *   anmelden" anbietet, statt pauschal den Server zu verdaechtigen;
	 * - **Verwerfen, das wirklich verwirft**: Der Dialog kuendigt an, die Arbeit
	 *   stehe danach nicht mehr zur Verfuegung — hier wurde bloss der
	 *   Offline-Modus verlassen, und beim naechsten Offline-Gehen kam alles als
	 *   veralteter Stand zurueck.
	 *
	 * `alleProjekte: true` ist bpwebs Eigenheit: Hier koennen mehrere Projekte
	 * gleichzeitig offline liegen, und „nur das aktuelle" waere ein stiller
	 * Datenverlust.
	 */
	async onSync(): Promise<void> {
		await this.offlineAbgleich.abgleichen({ alleProjekte: true });
	}

	openChangelog(): void {
		this.modalService.open(ChangelogComponent);
	}

	avatarCacheBust = 0;

	currentUserId(): string {
		return this.aus.currentUserId;
	}

	openProfile(): void {
		const ref = this.modalService.open(ProfileModalComponent, { size: 'lg', backdrop: 'static' });
		ref.result.then(
			(result) => {
				if (result?.refresh) {
					this.avatarCacheBust = Date.now();
					// Nach Upload/Delete neu fragen — sonst weiss die Navbar nicht,
					// ob jetzt ein Avatar existiert oder nicht.
					this.refreshOwnAvatarFlag();
				}
			},
			() => {},
		);
	}

	private refreshOwnAvatarFlag(): void {
		if (!this.profileApi) return;
		this.profileApi.getProfile().subscribe({
			next: p => { this.hasOwnAvatar = !!p.hasAvatar; },
			error: () => { /* schweigen — Avatar bleibt undefined, alter Pfad greift */ },
		});
	}
}
